import React from 'react';
import { Shield, Heart, Eye, Users } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const AboutPage = () => {
  const directors = [
    {
      name: 'Talluri Kalyan Theja',
      title: 'Director',
      description: 'Leading our mission with dedication to Dharma and social service.',
      image: 'https://customer-assets.emergentagent.com/job_hindu-service-hub/artifacts/kay910n9_kalyan%20theja.JPG',
    },
    {
      name: 'Talluri Phani Theja',
      title: 'Director',
      description: 'Committed to building temples and empowering communities.',
      image: 'https://customer-assets.emergentagent.com/job_hindu-service-hub/artifacts/kh53ly0d_Phani%20theja.jpg',
    },
  ];

  const values = [
    { icon: Shield, title: 'Dharma', desc: 'Upholding righteousness and spiritual values in all our endeavors.' },
    { icon: Heart, title: 'Seva', desc: 'Selfless service to humanity without any expectations.' },
    { icon: Eye, title: 'Transparency', desc: 'Complete accountability in fund utilization and operations.' },
    { icon: Users, title: 'Unity', desc: 'Bringing communities together for collective progress.' },
  ];

  return (
    <div className="min-h-screen bg-cream" data-testid="about-page">
      {/* Hero */}
      <section className="relative bg-terracotta py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="font-body text-sm uppercase tracking-wider text-saffron font-semibold">About Us</span>
            <h1 className="font-heading text-4xl md:text-6xl text-white mt-4 mb-6">
              Our Story & Mission
            </h1>
            <p className="font-body text-lg text-white/80 leading-relaxed">
              Dharma Astra Association was founded with a sacred purpose - to preserve Hindu Dharma through temple construction and serve humanity through social welfare.
            </p>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <img 
                src="https://images.pexels.com/photos/15902873/pexels-photo-15902873.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Temple Architecture"
                className="w-full h-[400px] object-cover rounded-2xl shadow-lg"
              />
            </div>
            <div className="space-y-8">
              <div>
                <h2 className="font-heading text-3xl text-gray-900 mb-4">Our Mission</h2>
                <p className="font-body text-gray-600 leading-relaxed">
                  To preserve and propagate Hindu Dharma by constructing sacred temples, particularly our flagship Lord Ram Temple project, while simultaneously serving society through education, women empowerment, food donation, and healthcare initiatives.
                </p>
              </div>
              <div>
                <h2 className="font-heading text-3xl text-gray-900 mb-4">Our Vision</h2>
                <p className="font-body text-gray-600 leading-relaxed">
                  To create a spiritually awakened and socially empowered India where every devotee has access to sacred spaces for worship, and every individual has opportunities for education, healthcare, and dignified living.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 bg-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="font-body text-sm uppercase tracking-wider text-saffron font-semibold">Our Foundation</span>
            <h2 className="font-heading text-3xl md:text-4xl text-gray-900 mt-4">Core Values</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <Card key={index} className="bg-white rounded-2xl border-none shadow-sm hover:shadow-md transition-all">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 bg-saffron/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <value.icon className="w-8 h-8 text-saffron" />
                  </div>
                  <h3 className="font-heading text-xl text-gray-900 mb-2">{value.title}</h3>
                  <p className="font-body text-sm text-gray-600">{value.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership */}
      <section className="py-20 bg-white" data-testid="leadership-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="font-body text-sm uppercase tracking-wider text-saffron font-semibold">Leadership</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mt-4">Our Directors</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 max-w-4xl mx-auto">
            {directors.map((director, index) => (
              <Card key={index} className="bg-cream rounded-2xl border-none shadow-sm overflow-hidden hover:shadow-md transition-shadow" data-testid={`director-card-${index}`}>
                <CardContent className="p-8 text-center">
                  <div className="w-40 h-40 mx-auto mb-6 rounded-2xl overflow-hidden shadow-md">
                    <img 
                      src={director.image} 
                      alt={director.name}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <h3 className="font-heading text-2xl font-bold text-gray-900 mb-1">{director.name}</h3>
                  <p className="font-body text-saffron font-semibold mb-3">{director.title}</p>
                  <p className="font-body text-gray-600 text-sm">{director.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Transparency Statement */}
      <section className="py-20 bg-saffron" data-testid="transparency-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Shield className="w-16 h-16 text-white mx-auto mb-6" />
          <h2 className="font-heading text-3xl md:text-4xl text-white mb-6">Our Commitment to Transparency</h2>
          <p className="font-body text-lg text-white/90 leading-relaxed mb-8">
            At Dharma Astra Association, we believe in complete transparency in all our operations. Every rupee donated is accounted for and utilized solely for the stated purposes - temple construction, cultural preservation, and social welfare activities.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-left">
            <div className="bg-white/10 backdrop-blur rounded-xl p-6">
              <h4 className="font-heading text-lg text-white mb-2">Financial Audits</h4>
              <p className="font-body text-sm text-white/80">Annual audits by certified chartered accountants ensure proper fund management.</p>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-xl p-6">
              <h4 className="font-heading text-lg text-white mb-2">Project Updates</h4>
              <p className="font-body text-sm text-white/80">Regular updates on project progress shared with all donors and stakeholders.</p>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-xl p-6">
              <h4 className="font-heading text-lg text-white mb-2">Tax Compliance</h4>
              <p className="font-body text-sm text-white/80">80G tax exemption certificates provided for all eligible donations.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
